import { useEffect, useState } from 'react'
import { Card, Badge, Button, SectionHeading, Divider, KVRow, Modal, Select, ColorSwatch } from '../../../shared/components/UI'
import { bobbinApi, unitsApi, shadesApi } from '../../../shared/lib/api'
import { History, Layers, Eye, Calendar, User, Package, Box, Filter, Trash2 } from 'lucide-react'

const FALLBACK = '-'

const STATUS_VARIANTS = {
  'created': 'blue',
  'processing': 'amber',
  'closed': 'green',
  'dispatched': 'gray'
}

export default function BobbinBatchesPage() {
  const [loading, setLoading] = useState(true)
  const [batches, setBatches] = useState([])
  const [total, setTotal]     = useState(0)
  const [filters, setFilters] = useState({ page: 1, unitId: '', shadeId: '', search: '' })
  const [pages, setPages]     = useState(1)
  const [units, setUnits]     = useState([])
  const [shades, setShades]   = useState([])
  
  const [selectedBatch, setSelectedBatch] = useState(null)

  const loadData = async () => {
    setLoading(true)
    try {
      const [batchRes, unitRes, shadeRes] = await Promise.all([
        bobbinApi.list(filters),
        unitsApi.list(),
        shadesApi.list()
      ])
      
      const { rows, total: count, pages: pCount } = batchRes.data.data
      setBatches(rows)
      setTotal(count)
      setPages(pCount)
      setUnits(unitRes.data.data)
      setShades(shadeRes.data.data)
    } catch (err) {
      console.error('Error loading bobbin batches:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadData() }, [filters.page, filters.unitId, filters.shadeId])

  const handleSearch = (e) => {
    if (e.key === 'Enter') loadData()
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800 tracking-tight">Bobbin Batches</h2>
          <p className="text-xs text-gray-400 mt-0.5 font-medium">Production batches currently in containers or being processed</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="blue" className="px-3 py-1">{total} Total Batches</Badge>
          <div className="w-px h-4 bg-gray-200 mx-1" />
          <Button variant="secondary" size="sm" onClick={loadData} className="rounded-xl border-gray-100 hover:bg-white shadow-sm transition-all">
            <History size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <Card bodyClass="p-4 bg-gray-50/50 border-gray-100/50">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
             <label className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 block ml-1">Search Batch #</label>
             <div className="relative group">
                <input 
                  className="form-input text-sm pl-4 bg-white border-gray-200 focus:ring-brand-500 rounded-xl h-10 w-full" 
                  placeholder="e.g. BT-0001" 
                  value={filters.search}
                  onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
                  onKeyDown={handleSearch}
                />
             </div>
          </div>
          
          <div className="w-48">
            <Select 
              label="Filter Unit"
              value={filters.unitId}
              onChange={e => setFilters(p => ({ ...p, unitId: e.target.value, page: 1 }))}
              options={[{ label: 'All Units', value: '' }, ...units.map(u => ({ label: u.name, value: u.id }))]}
              className="!mb-0 h-10 rounded-xl"
            />
          </div>

          <div className="w-64">
             <Select 
              label="Filter Shade"
              value={filters.shadeId}
              onChange={e => setFilters(p => ({ ...p, shadeId: e.target.value, page: 1 }))}
              options={[{ label: 'All Shades', value: '' }, ...shades.map(s => ({ label: `${s.name} (${s.code})`, value: s.id }))]}
              className="!mb-0 h-10 rounded-xl"
            />
          </div>

          <Button 
            variant="secondary" 
            onClick={() => setFilters({ page: 1, unitId: '', shadeId: '', search: '' })}
            className="h-10 rounded-xl px-4 border-gray-200 flex items-center gap-2"
          >
            <Filter size={14} />
            Reset
          </Button>
        </div>
      </Card>

      {/* Main Table */}
      <Card bodyClass="!p-0 overflow-hidden ring-1 ring-gray-100/50 shadow-sm rounded-2xl">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="w-[150px]">Batch #</th>
                <th>Shade</th>
                <th>Unit</th>
                <th>Container</th>
                <th>Weight</th>
                <th>Bobbins</th>
                <th className="text-center">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan="8" className="py-24 text-center text-gray-400 font-medium">Loading production batches...</td></tr>
              ) : batches.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-32 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-300">
                        <Box size={24} />
                      </div>
                      <p className="text-sm font-medium text-gray-400">No batches found matching your criteria.</p>
                    </div>
                  </td>
                </tr>
              ) : batches.map(batch => (
                <tr key={batch.id} className="group hover:bg-brand-50/20 transition-colors">
                  <td className="font-black text-brand-600 font-mono tracking-tight text-sm py-4">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-500 shadow-[0_0_8px_rgba(var(--brand-600),0.5)]" />
                        {batch.batch_number}
                      </div>
                      {batch.parent_batch_id && (
                        <Badge variant="amber" className="!text-[8px] px-1 py-0 w-fit mt-1">SPLIT</Badge>
                      )}
                    </div>
                  </td>
                  <td className="text-xs font-semibold text-gray-700">
                    <div className="flex items-center gap-2">
                       <ColorSwatch color={batch.shade?.color} size="xs" />
                       <div className="flex flex-col gap-0.5">
                          <span>{batch.shade?.name || FALLBACK}</span>
                          <span className="text-[9px] text-gray-400 uppercase font-mono tracking-tighter">{batch.shade?.code}</span>
                       </div>
                    </div>
                  </td>
                  <td className="text-xs text-gray-500 font-medium">{batch.unit?.name || FALLBACK}</td>
                  <td className="text-xs font-bold text-brand-700">
                    <div className="flex items-center gap-1.5">
                       <Archive size={12} className="text-gray-300" />
                       {batch.container?.container_id || FALLBACK}
                    </div>
                  </td>
                  <td className="text-sm font-black text-gray-800">
                    <div className="flex flex-col">
                      <span>{parseFloat(batch.gross_weight_kg || batch.total_weight_kg).toFixed(2)} <span className="text-[10px] text-gray-400 font-medium">KG Gross</span></span>
                      <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight -mt-1">Net: {parseFloat(batch.total_weight_kg).toFixed(2)} KG</span>
                    </div>
                  </td>
                  <td className="text-sm font-bold text-gray-900">
                    <Badge variant="blue" className="!bg-blue-50 !text-blue-700 border-blue-100/50">
                      {batch.bobbin_count || 0} pcs
                    </Badge>
                  </td>
                  <td className="text-center">
                    <div className="flex justify-center">
                      <Badge variant={STATUS_VARIANTS[batch.status] || 'gray'} className="uppercase text-[9px] tracking-widest font-black">
                        {batch.status}
                      </Badge>
                    </div>
                  </td>
                   <td>
                    <div className="flex justify-start items-center">
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={() => setSelectedBatch(batch)}
                        className="!p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 border-transparent hover:border-brand-100 rounded-xl"
                      >
                        <Eye size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-50 flex items-center justify-between bg-gray-50/30">
            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest leading-none">
              Page {filters.page} of {pages} <span className="mx-2 text-gray-200">/</span> {total} Batches
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" variant="secondary" 
                disabled={filters.page === 1}
                onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))}
                className="rounded-lg px-4"
              >Previous</Button>
              <Button 
                size="sm" variant="secondary" 
                disabled={filters.page === pages}
                onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))}
                className="rounded-lg px-4"
              >Next</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Batch Details Modal */}
      {selectedBatch && (
        <Modal
          isOpen={!!selectedBatch}
          onClose={() => setSelectedBatch(null)}
          title="Production Batch Details"
          size="md"
        >
          <div className="space-y-6">
            <div className="p-6 bg-brand-50/50 rounded-2xl border border-brand-100/50 flex flex-col items-center gap-3">
              <div className="w-14 h-14 bg-white rounded-2xl shadow-sm border border-brand-100 flex items-center justify-center text-brand-600">
                <Package size={28} />
              </div>
              <div className="text-center">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight font-mono leading-none">{selectedBatch.batch_number}</h3>
                 <p className="text-xs text-brand-600 font-bold mt-1 uppercase tracking-widest leading-none">Status: {selectedBatch.status}</p>
              </div>
              <div className="flex gap-2 mt-1">
                <Badge variant="blue" className="!bg-blue-100/50 font-black">{selectedBatch.bobbin_count} Bobbins</Badge>
                <div className="w-px h-4 bg-brand-200 self-center" />
                <Badge variant="green" className="!bg-emerald-100/50 font-black">Gross: {parseFloat(selectedBatch.gross_weight_kg || selectedBatch.total_weight_kg).toFixed(2)} KG</Badge>
                <div className="w-px h-4 bg-brand-200 self-center" />
                <Badge variant="blue" className="!bg-blue-100/50 font-black">Net: {parseFloat(selectedBatch.total_weight_kg).toFixed(2)} KG</Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 px-2">
               <div>
                  <SectionHeading>Source & Materials</SectionHeading>
                  <div className="space-y-2 mt-3">
                    <KVRow label="Shade" value={selectedBatch.shade?.name} />
                    <KVRow label="Shade Code" value={selectedBatch.shade?.code} />
                    <KVRow label="Source Roll" value={selectedBatch.roll?.parent?.roll_code || selectedBatch.roll?.roll_code} />
                    {selectedBatch.roll?.parent && (
                      <KVRow label="Sub Roll" value={selectedBatch.roll?.roll_code} />
                    )}
                    <KVRow label="Cutting Log" value={`#${selectedBatch.cuttingLog?.cutting_log_number || FALLBACK}`} />
                  </div>
               </div>
               <div>
                  <SectionHeading>Location & Tracking</SectionHeading>
                  <div className="space-y-2 mt-3">
                    <KVRow label="Current Unit" value={selectedBatch.unit?.name} />
                    <KVRow label="Container" value={selectedBatch.container?.container_id} bold />
                    {selectedBatch.parentBatch && (
                      <KVRow label="Splitted From" value={selectedBatch.parentBatch.batch_number} bold className="text-amber-600" />
                    )}
                    <KVRow label="Created At" value={new Date(selectedBatch.created_at).toLocaleDateString()} />
                    <KVRow label="Created By" value={selectedBatch.creator?.name} />
                  </div>
               </div>
            </div>

            {parseFloat(selectedBatch.wastage_kg || 0) > 0 && (
              <div className="mx-2 p-4 bg-red-50/50 rounded-2xl border border-red-100 flex flex-col gap-2 mb-6 shadow-sm">
                <div className="flex items-center gap-2 text-red-700">
                  <Trash2 size={16} />
                  <span className="text-[11px] font-black uppercase tracking-widest leading-none">Loss & Resolution</span>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] text-red-400 font-bold uppercase tracking-tight">Declared Wastage</span>
                    <span className="text-xs font-black text-red-700 tracking-tight">{selectedBatch.wastage_kg} KG</span>
                  </div>
                  <div className="flex flex-col gap-1 text-right">
                    <span className="text-[9px] text-red-400 font-bold uppercase tracking-tight">System Variance</span>
                    <span className="text-xs font-black text-red-700 tracking-tight">{selectedBatch.variance_pct || '0'}%</span>
                  </div>
                </div>
                <div className="pt-2 border-t border-red-100/50 mt-1">
                  <span className="text-[9px] text-red-400 font-bold uppercase tracking-tight block">Wastage Reason</span>
                  <span className="text-xs font-bold text-gray-700 leading-snug">
                    {(selectedBatch.wastage_reason === 'Other (specify below)' || selectedBatch.wastage_reason === 'Other') && selectedBatch.wastage_note 
                      ? selectedBatch.wastage_note 
                      : (selectedBatch.wastage_reason || 'Process Loss')}
                  </span>
                </div>
              </div>
            )}

            <Divider />

            <div className="pt-2">
               <Button onClick={() => setSelectedBatch(null)} className="w-full shadow-lg shadow-brand-200 py-3 rounded-2xl font-black">
                 Dismiss View
               </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

function Archive({ size, className }) {
   return (
    <svg 
      width={size} height={size} viewBox="0 0 24 24" fill="none" 
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
      className={className}
    >
      <rect width="20" height="5" x="2" y="3" rx="1"/>
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/>
      <path d="M10 12h4"/>
    </svg>
   )
}
